import { ref } from 'vue';
import type { Ref } from 'vue';
import type { StatusKind, StatusSnapshot } from '@/types/status';
import { fetchStatusSnapshot } from '@/services/statusService';

/** A simplified row type exposed to the tooltip component. */
export type StatusServiceRow = {
  id: 'ai' | 'deployment';
  name: string;
  status: StatusKind;
};

export type UseStatusTooltipReturn = {
  services: Ref<StatusServiceRow[]>;
  loading: Ref<boolean>;
  ensureLoaded: () => Promise<void>;
};

const CACHE_TTL_MS = 300_000; // 5 minutes
const SESSION_STORAGE_KEY = 'korum:status-tooltip-cache';

/** Safely read the persisted cache from sessionStorage (fallback: null). */
function readSessionCache(): { data: StatusSnapshot; fetchedAt: number } | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { data: StatusSnapshot; fetchedAt: number };
    if (!parsed.data || typeof parsed.fetchedAt !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Safely write the cache to sessionStorage (no-op if unavailable). */
function writeSessionCache(data: StatusSnapshot, fetchedAt: number): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ data, fetchedAt }));
  } catch {
    // sessionStorage unavailable (private browsing quota exceeded, etc.) — silently ignore
  }
}

/** Module-level cache — shared across all component instances. */
const cache: {
  data: StatusSnapshot | null;
  fetchedAt: number;
  inflight: Promise<StatusSnapshot> | null;
} = (() => {
  // Hydrate from sessionStorage on module load if still within TTL
  const persisted = readSessionCache();
  if (persisted && Date.now() - persisted.fetchedAt < CACHE_TTL_MS) {
    return { data: persisted.data, fetchedAt: persisted.fetchedAt, inflight: null };
  }
  return { data: null, fetchedAt: 0, inflight: null };
})();

/**
 * Provides status service rows for the footer tooltip.
 * Uses a module-level 5-minute cache with in-flight deduplication.
 * The cache is persisted to sessionStorage so it survives same-tab navigation.
 *
 * @returns services, loading, ensureLoaded
 */
export function useStatusTooltip(): UseStatusTooltipReturn {
  const services = ref<StatusServiceRow[]>([]);
  const loading = ref(false);

  /**
   * Ensures the status data is loaded and up-to-date.
   * Returns immediately if the cache is still valid (module-level or sessionStorage).
   * Deduplicates concurrent calls by sharing a single in-flight Promise.
   */
  const ensureLoaded = async (): Promise<void> => {
    const now = Date.now();
    if (cache.data && now - cache.fetchedAt < CACHE_TTL_MS) {
      services.value = cache.data.services.map((s) => ({
        id: s.id,
        name: s.name,
        status: s.status
      }));
      return;
    }

    if (!cache.inflight) {
      cache.inflight = fetchStatusSnapshot().finally(() => {
        cache.inflight = null;
      });
    }

    loading.value = true;
    try {
      const snapshot = await cache.inflight;
      cache.data = snapshot;
      cache.fetchedAt = Date.now();
      writeSessionCache(snapshot, cache.fetchedAt);
      services.value = snapshot.services.map((s) => ({
        id: s.id,
        name: s.name,
        status: s.status
      }));
    } catch {
      // On fetch failure, reflect each service as unknown
      services.value = [
        { id: 'ai', name: 'AI infrastructure', status: 'unknown' },
        { id: 'deployment', name: 'Deployment', status: 'unknown' }
      ];
    } finally {
      loading.value = false;
    }
  };

  return { services, loading, ensureLoaded };
}
